import React from 'react';
import { Alert, Box, Button, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { fmtMoney, plural } from '../../Documents/format';
import { BUCKET_COLORS } from '../constants';
import { deltaPct, fmtDay, fmtDelta, fmtDuration, fmtInt, fmtPct, fmtStamp, monthShort, share } from '../format';
import { BucketKey, JournalSummary } from '../types';

interface Props {
  data: JournalSummary | null;
  loading: boolean;
  error: string | null;
  bucket: BucketKey | '';
  onBucket: (bucket: BucketKey | '') => void;
  onRetry: () => void;
}

const BORDER = '#e2e8f0';

interface TileProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  delta?: React.ReactNode;
  color?: string;
  tooltip?: string;
  active?: boolean;
  onClick?: () => void;
}

const Tile: React.FC<TileProps> = ({ label, value, hint, delta, color, tooltip, active, onClick }) => {
  const body = (
    <Paper
      variant="outlined" onClick={onClick}
      sx={{
        px: 1.75, py: 1.25, borderRadius: 2, minWidth: 0, height: '100%', boxSizing: 'border-box',
        borderColor: active && color ? color : BORDER, bgcolor: active && color ? `${color}0d` : '#fff',
        cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s, background-color .15s',
        '&:hover': onClick ? { borderColor: color || '#94a3b8' } : undefined
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }} noWrap>{label}</Typography>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: color || '#0f172a', lineHeight: 1.35, mt: 0.25 }} noWrap>{value}</Typography>
      {delta && <Box sx={{ mt: 0.25 }}>{delta}</Box>}
      {hint && <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.25 }} noWrap>{hint}</Typography>}
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{body}</Tooltip> : body;
};

/** Изменение к прошлому месяцу: цвет - по смыслу показателя (рост просрочки - плохо) */
const Delta: React.FC<{ cur: number; prev?: number | null; month?: string; badWhenUp?: boolean }> = ({ cur, prev, month, badWhenUp }) => {
  const d = deltaPct(cur, prev);
  if (d === null || !month) return null;
  const up = d > 0;
  const color = Math.abs(d) < 0.05 ? '#64748b' : badWhenUp ? (up ? '#dc2626' : '#16a34a') : '#475569';
  return (
    <Typography sx={{ fontSize: '0.7rem', color, fontWeight: 600 }} noWrap>
      {up ? '▲' : d < 0 ? '▼' : ''} {fmtDelta(d)} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}>к {monthShort(month)}</Box>
    </Typography>
  );
};

/** Ключевые показатели по текущим фильтрам */
const JournalKpi: React.FC<Props> = ({ data, loading, error, bucket, onBucket, onRetry }) => {
  if (error && !data) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.25 }}>
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: 2 }} />)}
      </Box>
    );
  }

  const s = data.summary;
  const p = data.previous;
  const pm = p?.month;
  const d31to90Sum = Math.max(0, s.d90Sum - s.d30Sum);
  const d31to90Count = Math.max(0, s.d90Count - s.d30Count);
  const toggle = (key: BucketKey) => onBucket(bucket === key ? '' : key);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.25, opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
      <Tile
        label="Сумма в журнале" value={`${fmtMoney(s.sum)}`}
        delta={<Delta cur={s.sum} prev={p?.sum} month={pm} />}
        hint={`в среднем ${fmtMoney(s.avgSum)} на запись`}
      />
      <Tile
        label="Записей" value={fmtInt(s.count)}
        delta={<Delta cur={s.count} prev={p?.count} month={pm} />}
        hint={s.edited ? `изменено после ввода: ${fmtInt(s.edited)}` : 'без изменений после ввода'}
        tooltip="Записи, у которых дата изменения позже даты ввода, учтены как изменённые"
      />
      <Tile
        label="Количество" value={`${fmtInt(s.qty)} уп.`}
        hint={`${fmtInt(s.qtyPcs)} шт. · ${fmtInt(s.serials)} ${plural(s.serials, 'серия', 'серии', 'серий')}`}
      />
      <Tile
        label="Аптек с записями" value={`${fmtInt(s.pharmacies)} из ${fmtInt(data.coverage.zone)}`}
        hint={data.coverage.inactive
          ? <Box component="span" sx={{ color: '#dc2626' }}>без записей: {fmtInt(data.coverage.inactive)}</Box>
          : 'все аптеки зоны внесли данные'}
        tooltip="Аптеки зоны видимости с активной учётной записью; подробно - «Аналитика» → «Без записей»"
      />
      <Tile
        label="Товаров" value={fmtInt(s.products)}
        hint={`производителей: ${fmtInt(s.producers)}`}
      />
      <Tile
        label="Просрочено" value={`${fmtMoney(s.expiredSum)}`} color={BUCKET_COLORS.expired.color}
        active={bucket === 'expired'} onClick={() => toggle('expired')}
        delta={<Delta cur={s.expiredSum} prev={p?.expiredSum} month={pm} badWhenUp />}
        hint={`${fmtInt(s.expiredCount)} зап. · ${fmtPct(share(s.expiredSum, s.sum))} суммы`}
        tooltip={bucket === 'expired' ? 'Снять фильтр «Просрочено»' : 'Показать только просроченные записи'}
      />
      <Tile
        label="Истекает за 30 дней" value={`${fmtMoney(s.d30Sum)}`} color={BUCKET_COLORS.d30.color}
        active={bucket === 'd30'} onClick={() => toggle('d30')}
        delta={<Delta cur={s.d30Sum} prev={p?.d30Sum} month={pm} badWhenUp />}
        hint={`${fmtInt(s.d30Count)} зап. · 31–90 дн.: ${fmtMoney(d31to90Sum)} (${fmtInt(d31to90Count)})`}
        tooltip={bucket === 'd30' ? 'Снять фильтр «До 30 дней»' : 'Показать записи со сроком до 30 дней'}
      />
      <Tile
        label="Ближайший срок" value={fmtDay(s.nearestExpiry)}
        hint={`ср. остаток ${s.avgDaysLeft === null ? '-' : fmtDuration(s.avgDaysLeft)} · запись ${fmtStamp(s.lastRecord)}`}
        tooltip="Ближайшая ещё не истёкшая дата и средний остаток срока непросроченных записей; справа - последняя запись"
      />
    </Box>
  );
};

export default React.memo(JournalKpi);
